import { useCountryConfig } from 'queries/user/useCountryConfig';
import React, { useRef } from 'react';
import Button from '@teetsh/app/src/components/Buttons/Button';
import Dialog from '@teetsh/app/src/components/Dialog';
import Editor from '@teetsh/app/src/components/Editor/formWrapper';
import { Radios } from '@teetsh/app/src/components/Form/Radios';
import DateInput from '@teetsh/app/src/components/Form/Dateinput2';
import FormGroup from '@teetsh/app/src/components/Form/FormGroup';
import Input from '@teetsh/app/src/components/Form/InputGroup/formWrapper';
import useClassrooms from '@teetsh/app/src/containers/App/context/ClassroomsContext/useClassrooms';
import { Field, Form } from 'react-final-form';
import useElevesCustomFields from '@teetsh/app/src/queries/eleves/customFields/useElevesCustomFields';
import { PlusIcon as Plus } from '@heroicons/react/24/outline';
import Popover from '@teetsh/app/src/components/Popover';
import { AddNewFieldPopoverWrapper } from './AddNewField';
import { NewCustomFields } from '@teetsh/app/src/components/NewCustomFields';
import useUpdateElevesCustomFields from '@teetsh/app/src/queries/eleves/customFields/useUpdateElevesCustomFields';
import _isEmpty from 'lodash-es/isEmpty';
import { useTranslation } from 'react-i18next';
import { useTranslatedValidation } from 'utils/useTranslatedValidation';
import { Decloisonnement } from 'containers/Eleves/EleveDetails/Main/General/Decloisonnement';
import ScreenSpinner from 'components/Spinner/ScreenSpinner';
import tw from 'twin.macro';
import { UserLocale } from '@teetsh/app/src/types/user';
import { EleveGroupSelector } from '@teetsh/app/src/components/EleveGroupSelector';
import { FormApi } from 'final-form';

interface Props {
  initialValues: any;
  onSubmit: (v: any) => Promise<any>;
  onClose: () => void;
  viewId: string;
}

export function EleveForm({ initialValues, onClose, onSubmit, viewId }: Props) {
  const inputEl = useRef(null);
  const { data, isLoading } = useElevesCustomFields();
  const updateCustomFields = useUpdateElevesCustomFields();
  const { t } = useTranslation(['eleve', 'common']);
  const { required, hasXcharacters } = useTranslatedValidation();
  const buttonRef = useRef();
  const classrooms = useClassrooms();
  const { isFieldIneEnabled } = useCountryConfig();

  const handleSubmitAndClose = async (form: FormApi, handleSubmit: any) => {
    const promise = handleSubmit();

    if (promise) {
      await promise;
      onClose();
    }
  };

  const handleSubmitAndCreateOther = async (
    form: FormApi,
    handleSubmit: any,
  ) => {
    const promise = handleSubmit();

    if (promise) {
      await promise;
      form.restart(initialValues);
      (inputEl.current as any).focus();
    }
  };

  if (isLoading && !data) {
    return <ScreenSpinner />;
  }

  return (
    <Form
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({ form, handleSubmit, submitting }) => (
        <form>
          <Dialog.Body>
            <div tw="border-b border-neutral-quiet mb-4 pb-4">
              <div
                css={[
                  tw`grid gap-4`,
                  {
                    gridTemplateColumns: '1fr 90px',
                    gridTemplateAreas: `
                      "givenName givenName"
                      "familyName familyName"
                      "birthday gender"
                      "ine ine"
                    `,
                    '@media (min-width: 640px)': {
                      gridTemplateColumns: '1fr 1fr 90px',
                      gridTemplateAreas: `
                        "givenName familyName gender"
                        "birthday ine gender"
                      `,
                    },
                  },
                ]}
              >
                <div css={{ gridArea: 'givenName' }}>
                  <Field
                    validate={required}
                    id="givenName"
                    inputRef={inputEl}
                    autoFocus
                    name="givenName"
                    label={`${t('eleve:fields.givenName')}*`}
                    autoComplete="off"
                    component={Input}
                  />
                </div>
                <div css={{ gridArea: 'familyName' }}>
                  <Field
                    validate={required}
                    id="familyName"
                    name="familyName"
                    label={`${t('eleve:fields.familyName')}*`}
                    autoComplete="off"
                    component={Input}
                  />
                </div>
                <div css={{ gridArea: 'birthday' }}>
                  <Field
                    id="birthday"
                    name="birthday"
                    render={({ input: { value, onChange } }) => (
                      <DateInput
                        withoutMargin
                        userLocale={t('common:locale.long') as UserLocale}
                        label={t('eleve:fields.birthday')}
                        canBeNull
                        id="birthday"
                        value={value === '' ? null : value}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div css={{ gridArea: 'gender' }}>
                  <Field
                    validate={required}
                    name="gender"
                    render={({ input: { value, onChange } }) => (
                      <Radios
                        required
                        name="gender"
                        value={value}
                        onChange={onChange}
                        legendStyle={tw`mb-1`}
                        options={[
                          {
                            label: t('eleve:fields.boy'),
                            value: 'H',
                          },
                          {
                            label: t('eleve:fields.girl'),
                            value: 'F',
                          },
                        ]}
                        legendVariant="intense"
                        label={t('eleve:fields.gender')}
                      />
                    )}
                  />
                </div>
                {isFieldIneEnabled && (
                  <div css={{ gridArea: 'ine' }}>
                    <Field
                      name="ine"
                      label={t('eleve:fields.ine')}
                      id="ine"
                      validate={hasXcharacters(11)}
                      type="text"
                      placeholder={t('eleve:fields.inePlaceholder')}
                      helperText={t('eleve:fields.ineHelperText')}
                      component={Input}
                    />
                  </div>
                )}
              </div>
            </div>

            <div tw="mb-4 border-b pb-4 border-neutral-quiet">
              <div
                css={[
                  tw`grid gap-4 mb-4`,
                  {
                    gridTemplateColumns: '1fr 90px',
                    gridTemplateAreas: `
                      "groups classroom"
                      "arrivalDate arrivalDate"
                      "radiationDate radiationDate"
                      "decloisonnement decloisonnement"
                    `,
                    '@media (min-width: 640px)': {
                      gridTemplateColumns: '1fr 1fr 90px',
                      gridTemplateAreas: `
                        "groups groups classroom"
                        "arrivalDate radiationDate classroom"
                        "decloisonnement decloisonnement decloisonnement"
                      `,
                    },
                  },
                ]}
              >
                <div css={{ gridArea: 'groups' }}>
                  <Field
                    id="groups"
                    name="groups"
                    render={({ input: { value, onChange } }) => (
                      <FormGroup
                        label={t('eleve:fields.group')}
                        withoutMargin
                        helperText={t('eleve:fields.groupHelperText')}
                      >
                        <EleveGroupSelector value={value} onChange={onChange} />
                      </FormGroup>
                    )}
                  />
                </div>
                <div css={{ gridArea: 'classroom' }}>
                  <Field
                    name="classroomId"
                    render={({ input: { value, onChange } }) => (
                      <Radios
                        legendStyle={tw`mb-1`}
                        legendVariant="intense"
                        value={value}
                        onChange={v => {
                          form.change(
                            'groupName',
                            classrooms
                              .map(cl => ({
                                label: cl.name,
                                value: cl.id,
                              }))
                              .find(o => o.value === v)?.label,
                          );
                          return onChange(v);
                        }}
                        label={t('eleve:fields.classroom')}
                        options={classrooms.map(cl => ({
                          label: cl.name,
                          value: cl.id,
                        }))}
                      />
                    )}
                  />
                </div>
                <div css={{ gridArea: 'arrivalDate' }}>
                  <Field
                    id="arrivalDate"
                    name="arrivalDate"
                    render={({ input: { value, onChange } }) => (
                      <DateInput
                        withoutMargin
                        userLocale={t('common:locale.long') as UserLocale}
                        label={t('eleve:fields.arrivalDate')}
                        placeholder={t('common:datePlaceholder')}
                        canBeNull
                        id="arrivalDate"
                        value={value === '' ? null : value}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div css={{ gridArea: 'radiationDate' }}>
                  <Field
                    id="radiationDate"
                    name="radiationDate"
                    render={({ input: { value, onChange } }) => (
                      <DateInput
                        withoutMargin
                        userLocale={t('common:locale.long') as UserLocale}
                        label={t('eleve:fields.radiationDate')}
                        placeholder={t('common:datePlaceholder')}
                        canBeNull
                        id="radiationDate"
                        value={value === '' ? null : value}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div css={{ gridArea: 'decloisonnement' }}>
                  <Field
                    id="decloisonement"
                    name="decloisonement"
                    label={t('eleve:fields.classeDecloisonnement')}
                    allowNull
                    parse={value => value}
                    render={({ input: { value, onChange } }) => (
                      <Decloisonnement
                        value={value}
                        onChange={v => onChange(v)}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            <div tw="mb-4">
              <FormGroup label={t('eleve:fields.remark')}>
                <Field
                  name="comment"
                  label={t('eleve:fields.remark')}
                  inline
                  component={Editor}
                />
              </FormGroup>
            </div>
            <Field
              name="customFields"
              render={({ input: { value, onChange } }) => {
                const cFields = data || [];
                return (
                  <div tw="space-y-4">
                    {cFields.map(cField => {
                      const matchingField = (value || []).find(
                        v => v.id === cField.id,
                      );
                      return (
                        <NewCustomFields
                          key={cField.id}
                          customFieldDefinition={cField}
                          value={matchingField?.value}
                          options={(cField.options || []).map(opt => ({
                            value: opt.value,
                            id: opt.id,
                            color: opt.color,
                          }))}
                          onUpdateCustomField={updateCustomFields}
                          onChange={v => {
                            // The component can handle arrays but for now we only want to store a single value
                            const fieldValue = v;
                            if (matchingField === undefined) {
                              return onChange([
                                ...value,
                                {
                                  id: cField.id,
                                  value: fieldValue,
                                },
                              ]);
                            }
                            if (
                              v === undefined ||
                              (cField.type !== 'number' && _isEmpty(v))
                            ) {
                              return onChange(
                                value.filter(p => matchingField?.id !== p.id),
                              );
                            }
                            return onChange(
                              value.map(p => {
                                if (matchingField?.id === p.id) {
                                  return {
                                    id: p.id,
                                    value: fieldValue,
                                  };
                                }
                                return p;
                              }),
                            );
                          }}
                        />
                      );
                    })}
                  </div>
                );
              }}
            />
            <div tw="mt-1">
              <Popover portal>
                <Popover.Button outlined icon={Plus}>
                  {t('eleve:actions.addField')}
                </Popover.Button>
                <Popover.Panel overflow="visible" size="xl">
                  <div tw="p-4 max-h-[60vh] overflow-y-auto">
                    <AddNewFieldPopoverWrapper
                      viewId={viewId}
                      onClose={() => {
                        (buttonRef.current as any).click();
                      }}
                    />
                  </div>
                </Popover.Panel>
              </Popover>
            </div>
          </Dialog.Body>
          <Dialog.Footer>
            <Button
              large
              onClick={onClose}
              minimal
              disabled={submitting}
              type="button"
            >
              {t('common:buttons.cancel')}
            </Button>
            <Button
              large
              outlined
              loading={submitting}
              onClick={() => handleSubmitAndClose(form, handleSubmit)}
              type="button"
            >
              {t('eleve:actions.addEleveAndClose')}
            </Button>
            <Button
              large
              intent="primary"
              loading={submitting}
              onClick={() => handleSubmitAndCreateOther(form, handleSubmit)}
              type="button"
            >
              {t('eleve:actions.addEleveAndCreateOther')}
            </Button>
          </Dialog.Footer>
        </form>
      )}
    />
  );
}
